'use client';

import { c as _c } from "react/compiler-runtime";
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { Children, type ReactNode, useEffect, useMemo } from 'react';
import { ENTRANCE_EASING, INTERSTITIAL_EXIT_CARD_DURATION, INTERSTITIAL_EXIT_CARD_STAGGER } from '../../motion';
const INITIAL_DELAY = 0.3;
const STAGGER_DELAY = 0.05;
const ENTER_DURATION = 0.5;
const INITIAL_SCALE = 0.95;
// The exit is a dismissal, not a reveal — it runs the entrance in reverse but
// compressed so it clears fast. The overlay's unmount is derived from these same
// tokens (see motion.ts), so the reveal always waits for this to finish.
const EXIT_DURATION = INTERSTITIAL_EXIT_CARD_DURATION;
const EXIT_STAGGER_DELAY = INTERSTITIAL_EXIT_CARD_STAGGER;
type CirclingElementsProps = {
  children: ReactNode;
  className?: string;
  direction?: 'normal' | 'reverse';
  duration?: number;
  isExiting?: boolean;
  paused?: boolean;
  radius?: number;
  // Render the items already entered, e.g. when they were on screen in the
  // server HTML before hydration.
  skipEntrance?: boolean;
};
function CirclingItem(t0) {
  const $ = _c(24);
  const {
    child,
    offset,
    radius,
    progress,
    direction,
    totalItems,
    isExiting,
    skipEntrance
  } = t0;
  const directionMultiplier = direction === "reverse" ? -1 : 1;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [0, 1];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = offset + 360 * directionMultiplier;
  let t3;
  if ($[1] !== offset || $[2] !== t2) {
    t3 = [offset, t2];
    $[1] = offset;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const angle = useTransform(progress, t1, t3);
  let t4;
  if ($[4] !== radius) {
    t4 = a => {
      const radians = a * Math.PI / 180;
      return Math.cos(radians) * radius;
    };
    $[4] = radius;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const x = useTransform(angle, t4);
  let t5;
  if ($[6] !== radius) {
    t5 = a_0 => {
      const radians_0 = a_0 * Math.PI / 180;
      return Math.sin(radians_0) * radius;
    };
    $[6] = radius;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const y = useTransform(angle, t5);
  const angleFrom10OClock = (offset + 150) % 360;
  const clockwiseIndex = Math.round(angleFrom10OClock / 360 * totalItems) % totalItems;
  const exitStaggerIndex = totalItems - 1 - clockwiseIndex;
  const exitDelay = exitStaggerIndex * EXIT_STAGGER_DELAY;
  let t6;
  if ($[8] !== isExiting) {
    t6 = isExiting ? {
      opacity: 0,
      scale: INITIAL_SCALE
    } : {
      opacity: 1,
      scale: 1
    };
    $[8] = isExiting;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== skipEntrance) {
    t7 = skipEntrance ? false : {
      opacity: 0,
      scale: INITIAL_SCALE
    };
    $[10] = skipEntrance;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== x || $[13] !== y) {
    t8 = {
      x,
      y,
      translateX: "-50%",
      translateY: "-50%"
    };
    $[12] = x;
    $[13] = y;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = isExiting ? exitDelay : INITIAL_DELAY + clockwiseIndex * STAGGER_DELAY;
  const t10 = isExiting ? EXIT_DURATION : ENTER_DURATION;
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = {
      delay: t9,
      duration: t10,
      ease: ENTRANCE_EASING
    };
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== child || $[19] !== t11 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t12 = <motion.div animate={t6} className="absolute top-1/2 left-1/2 transform-gpu" initial={t7} style={t8} transition={t11}>{child}</motion.div>;
    $[18] = child;
    $[19] = t11;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}
export function CirclingElements(t0) {
  const $ = _c(25);
  const {
    children,
    radius: t1,
    duration: t2,
    direction: t3,
    className,
    paused: t4,
    isExiting: t5,
    skipEntrance: t6
  } = t0;
  const radius = t1 === undefined ? 250 : t1;
  const duration = t2 === undefined ? 60 : t2;
  const direction = t3 === undefined ? "normal" : t3;
  const paused = t4 === undefined ? false : t4;
  const isExiting = t5 === undefined ? false : t5;
  const skipEntrance = t6 === undefined ? false : t6;
  let t7;
  if ($[0] !== children) {
    t7 = Children.toArray(children).map(_temp);
    $[0] = children;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const childItems = t7;
  const childCount = childItems.length;
  const progress = useMotionValue(0);
  let t8;
  let t9;
  if ($[2] !== duration || $[3] !== paused || $[4] !== progress) {
    t8 = () => {
      if (paused) {
        return;
      }
      const controls = animate(progress, 1, {
        duration,
        ease: "linear",
        repeat: Number.POSITIVE_INFINITY,
        repeatType: "loop"
      });
      return () => controls.stop();
    };
    t9 = [progress, duration, paused];
    $[2] = duration;
    $[3] = paused;
    $[4] = progress;
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  useEffect(t8, t9);
  const t10 = `relative${className ? ` ${className}` : ""}`;
  let t11;
  if ($[7] !== childCount || $[8] !== childItems || $[9] !== direction || $[10] !== isExiting || $[11] !== progress || $[12] !== radius || $[13] !== skipEntrance) {
    let t12;
    if ($[15] !== childCount || $[16] !== direction || $[17] !== isExiting || $[18] !== progress || $[19] !== radius || $[20] !== skipEntrance) {
      t12 = (item, index) => {
        const offset = index * 360 / childCount;
        return <CirclingItem child={item.child} direction={direction} index={index} isExiting={isExiting} key={item.key} offset={offset} progress={progress} radius={radius} skipEntrance={skipEntrance} totalItems={childCount} />;
      };
      $[15] = childCount;
      $[16] = direction;
      $[17] = isExiting;
      $[18] = progress;
      $[19] = radius;
      $[20] = skipEntrance;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t11 = childItems.map(t12);
    $[7] = childCount;
    $[8] = childItems;
    $[9] = direction;
    $[10] = isExiting;
    $[11] = progress;
    $[12] = radius;
    $[13] = skipEntrance;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp(child, i) {
  return {
    child,
    key: `circling-${String(i)}`
  };
}